import type { PhisherConfig, RemotePluginConfig, ReportAction } from '../models/PhisherConfig';
import { DEFAULT_CONFIG } from '../models/PhisherConfig';

/**
 * Service for loading and managing plugin configuration.
 * Ports PhisherConfigProvider.cs functionality.
 */
export class ConfigService {
  private static readonly STORAGE_KEY = 'phisherConfig';
  private static readonly CONFIG_ENDPOINT = '/plugin';

  private static cachedConfig: PhisherConfig | null = null;

  /**
   * Load configuration from local storage and remote server.
   * Merges remote config with local settings (remote takes precedence).
   * @returns Complete configuration
   */
  static async loadConfig(): Promise<PhisherConfig> {
    // Start with defaults
    let config: PhisherConfig = { ...DEFAULT_CONFIG };

    // Try to load from local storage
    const localConfig = this.loadLocalConfig();
    if (localConfig) {
      config = { ...config, ...localConfig };
    }

    // Try to fetch remote configuration
    try {
      const remoteConfig = await this.fetchRemoteConfig(config.baseUrl);
      if (remoteConfig) {
        config = this.mergeRemoteConfig(config, remoteConfig);
      }
    } catch (error) {
      console.warn('Failed to fetch remote config:', error);
      // Continue with local/default config
    }

    // Cache the config
    this.cachedConfig = config;

    return config;
  }

  /**
   * Get cached configuration (or load if not cached).
   */
  static async getConfig(): Promise<PhisherConfig> {
    if (this.cachedConfig) {
      return this.cachedConfig;
    }
    return this.loadConfig();
  }

  /**
   * Fetch configuration from remote /plugin endpoint.
   */
  private static async fetchRemoteConfig(baseUrl: string): Promise<RemotePluginConfig | null> {
    if (!baseUrl) {
      return null;
    }

    const url = `${baseUrl.replace(/\/+$/, '')}${this.CONFIG_ENDPOINT}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
        // Add timeout
        signal: AbortSignal.timeout(10000),
      });

      if (!response.ok) {
        console.warn(`Remote config returned ${response.status}`);
        return null;
      }

      const data = await response.json();
      return data as RemotePluginConfig;
    } catch (error) {
      console.warn('Error fetching remote config:', error);
      return null;
    }
  }

  /**
   * Load configuration from local storage.
   */
  private static loadLocalConfig(): Partial<PhisherConfig> | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (error) {
      console.warn('Error loading local config:', error);
    }
    return null;
  }

  /**
   * Save configuration to local storage.
   */
  static saveLocalConfig(config: Partial<PhisherConfig>): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(config));
    } catch (error) {
      console.warn('Error saving local config:', error);
    }
  }

  /**
   * Merge remote config with local config.
   * Remote values take precedence if present.
   */
  private static mergeRemoteConfig(
    local: PhisherConfig,
    remote: RemotePluginConfig
  ): PhisherConfig {
    // Resolve reportAction from backend "Method" field or direct override
    const resolvedAction = (remote.reportAction ?? remote.Method) as ReportAction | undefined;
    // Resolve toAddress from backend "Email" field or direct override
    const resolvedAddress = remote.toAddress ?? remote.Email;
    // Resolve deleteAfterReport — backend sends string "True"/"False"
    const resolvedDelete =
      remote.deleteAfterReport !== undefined
        ? remote.deleteAfterReport
        : remote.DeleteAfterReport !== undefined
          ? remote.DeleteAfterReport.toLowerCase() === 'true'
          : undefined;

    return {
      ...local,
      ...(remote.reportPath !== undefined && { reportPath: remote.reportPath }),
      ...(remote.apiKey !== undefined && { apiKey: remote.apiKey }),
      ...(remote.apiHeaderName !== undefined && { apiHeaderName: remote.apiHeaderName }),
      ...(resolvedAddress !== undefined && { toAddress: resolvedAddress }),
      ...(resolvedAction !== undefined && { reportAction: resolvedAction }),
      ...(resolvedDelete !== undefined && { deleteAfterReport: resolvedDelete }),
    };
  }

  /**
   * Increment the reports counter and save.
   */
  static incrementReportsCounter(): number {
    if (this.cachedConfig) {
      this.cachedConfig.reportsCounter++;
      this.saveLocalConfig({ reportsCounter: this.cachedConfig.reportsCounter });
      return this.cachedConfig.reportsCounter;
    }
    return 0;
  }

  /**
   * Update base URL (for configuration UI).
   */
  static updateBaseUrl(baseUrl: string): void {
    if (this.cachedConfig) {
      this.cachedConfig.baseUrl = baseUrl;
    }
    this.saveLocalConfig({ baseUrl });
  }

  /**
   * Clear cached configuration.
   */
  static clearCache(): void {
    this.cachedConfig = null;
  }

  /**
   * Validate configuration.
   * @returns Array of validation error messages (empty if valid)
   */
  static validate(config: PhisherConfig): string[] {
    const errors: string[] = [];

    if (!config.baseUrl || config.baseUrl.trim().length === 0) {
      errors.push('Base URL is required');
    } else {
      try {
        new URL(config.baseUrl);
      } catch {
        errors.push('Base URL is not a valid URL');
      }
    }

    if (!config.reportPath || !config.reportPath.includes('{id}')) {
      errors.push('Report path must include {id} placeholder');
    }

    return errors;
  }
}
